import {
  ColumnType,
  IntegrationStatus,
  ResumeStatus,
  defaultHistoricSyncInterval,
  getFieldMappingsMocData,
  supportedObjectsToFieldIds,
  getEntityIdMap,
  IntegrationsTools,
  ZERO_VALUE_OPERATORS,
} from 'src/features/settings/integrations/constants/integrations';
import {
  ITableData,
  IntegrationStateType,
  MapValuesType,
  RowType,
  IntegrationTableColumn,
  FieldTypes,
  IntegrationType,
  SyncDirection,
} from 'src/features/settings/integrations/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  GlobalSyncFlagResult,
  PollingFrequencies,
  SyncCondition,
} from 'src/store/integrations-v2/types';
import { timezoneConverter } from 'src/utils/timezonesConvertor';

dayjs.extend(relativeTime);

export const getIntegraionStage = (integrationStatus: string) => {
  let result: IntegrationStateType = 'disconnected';
  if (ResumeStatus.includes(integrationStatus)) result = 'resume';
  if (integrationStatus === IntegrationStatus.CONFIGURED) result = 'manage';

  return result;
};

export const getIntegrationSteps = (
  label: string,
  type: IntegrationType,
  supportedStage?: string[],
) => {
  // Stage configuration mapping
  const stageConfig = {
    USER_MAPPING_STAGE: {
      label: 'User mapping',
      desc: `Link Sprouts users to ${label} users to sync data for mapped users.`,
    },
    SYNC_RULE_SETUP: {
      label: 'Sync scenario',
      desc: 'Create sync rules to get the right data at the right platform',
    },
    ENTITY_MAPPING_STAGE: {
      label: 'Field mapping',
      desc: `Map Sprouts fields to ${label} fields.`,
    },
    TEST_STAGE: {
      label: 'Test & confirm',
      desc: `Review the test data as received from ${label}.`,
    },
  };

  // If supportedStage is provided, use config-driven approach
  if (supportedStage && supportedStage.length > 0) {
    return supportedStage
      .map((stage) => stageConfig[stage as keyof typeof stageConfig])
      .filter(Boolean);
  }

  // Fallback to type-based logic for backward compatibility
  let steps = [
    stageConfig.USER_MAPPING_STAGE,
    stageConfig.SYNC_RULE_SETUP,
    stageConfig.ENTITY_MAPPING_STAGE,
    stageConfig.TEST_STAGE,
  ];

  if (type === 'CMP') {
    steps = steps.filter((step) => !['Sync scenario'].includes(step.label));
  }

  return steps;
};

export const getUserMappingsColumns: (label: string) => IntegrationTableColumn[] = (label) => [
  {
    label: 'Status',
    id: 'status',
    type: ColumnType.Status,
    width: 38,
  },
  {
    label: 'Sprouts user Email ID',
    id: 'sproutsUserEmailId',
    width: 475,
    type: ColumnType.Text,
  },
  {
    label: `${label} user Email ID`,
    id: 'integrationUserEmailId',
    width: 475,
    type: ColumnType.AutoComplete,
    autoCompleteplaceholder: 'Choose an email',
  },
];
export const getFieldMappingsColumns: (
  label: string,
  providerType: IntegrationType,
) => IntegrationTableColumn[] = (label, providerType) => {
  const cols = [
    {
      label: 'Status',
      id: 'status',
      type: ColumnType.Status,
      width: 38,
    },
    {
      label: 'Sprouts field',
      id: 'sproutsField',
      type: ColumnType.Text,
      width: 212,
    },
    {
      label: `${label} field`,
      id: 'integrationField',
      type: ColumnType.AutoComplete,
      autoCompleteplaceholder: 'Choose field',
      width: 212,
    },
    {
      label: 'Map values',
      id: 'mapValues',
      type: ColumnType.ValueMapIcon,
      width: 132,
    },
    {
      label: `Sprouts to ${providerType}`,
      id: 'sproutsToCrm',
      type: ColumnType.CheckboxWithDorpdown,
      width: 260,
    },
    {
      label: `${providerType} to Sprouts`,
      id: 'crmToSprouts',
      type: ColumnType.Checkbox,
      width: 116,
    },
  ];

  return cols;
};

export const getPreviousMonthDate = (date: Date, months: number) => {
  date.setMonth(date.getMonth() - months);
  return date;
};

export const getRequiredFieldsLeft = (rowData: RowType[]) => {
  return (
    rowData?.filter((field) => {
      if (field.required) {
        if (field.optionalWithValue) {
          const check = rowData?.some(
            (field) => field.optionalWithValue && !field.typeMistachError && field.integrationField,
          );
          return (!getOptionalSelectedField(field, rowData) || field.typeMistachError) && !check;
        }
        return !field.status || field.typeMistachError;
      }
      return false;
    })?.length || 0
  );
};
export const getMappedFieldsCount = (rowData: RowType[]) => {
  return rowData?.filter((field) => field.status)?.length || 0;
};

export const getRequiredSproutsFieldsMissing = (rowData: RowType[]): RowType[] => {
  return (
    rowData?.filter((field) => {
      if (!field.required) return false;
      if (field.optionalWithValue) {
        const check = rowData?.some(
          (f) => f.optionalWithValue && !f.typeMistachError && f.integrationField,
        );
        return (!getOptionalSelectedField(field, rowData) || field.typeMistachError) && !check;
      }
      return !field.status || field.typeMistachError;
    }) || []
  );
};

export const getFilteredFieldMappingsMocData = (
  globalSyncFlag: Record<string, any>,
  supportedObjects: string[] = [],
  providerName?: string,
) => {
  const supportedMappingIds = supportedObjects
    .map((obj) => supportedObjectsToFieldIds[obj])
    .filter(Boolean);

  // If no supportedObjects provided, return empty array (no field mappings needed)
  if (supportedMappingIds.length === 0) {
    return [];
  }

  const fieldMappings = getFieldMappingsMocData(providerName);

  // Filter entities based on sync scenario settings
  // This filters entire entities (call, meeting, task, email, lead) based on their enable flags
  return fieldMappings.filter(
    (field) =>
      supportedMappingIds.includes(field.id) &&
      (field.id !== 'lead' || globalSyncFlag.isLeadEnabled) &&
      (field.id !== 'opportunity' || globalSyncFlag.dealEnabled !== false) &&
      (field.id !== 'email' ||
        (globalSyncFlag.emailEnabled && !globalSyncFlag.emailAsTaskEnabled)) &&
      (field.id !== 'call' || globalSyncFlag.callEnabled !== false) &&
      (field.id !== 'meeting' || globalSyncFlag.meetingEnabled !== false) &&
      (field.id !== 'task' ||
        globalSyncFlag.taskEnabled !== false ||
        globalSyncFlag.emailAsTaskEnabled === true),
  );
};

type ActivityFlags = {
  callEnabled?: boolean;
  meetingEnabled?: boolean;
  taskEnabled?: boolean;
  emailEnabled?: boolean;
  emailAsTaskEnabled?: boolean;
};

/**
 * Filter supported activity/object names using sync-scenario activity flags.
 * Keeps non-activity entities untouched, and only filters activity entities when flags are provided.
 */
export const getFilteredSupportedActivity = (
  supportedActivity: string[] = [],
  activityFlags?: ActivityFlags,
): string[] => {
  if (!activityFlags) {
    return supportedActivity;
  }

  const hasAnyConfiguredFlag = [
    activityFlags.callEnabled,
    activityFlags.meetingEnabled,
    activityFlags.taskEnabled,
    activityFlags.emailEnabled,
    activityFlags.emailAsTaskEnabled,
  ].some((value) => typeof value === 'boolean');

  if (!hasAnyConfiguredFlag) {
    return supportedActivity;
  }

  const hasCallsEntity = supportedActivity.includes('Calls');

  return supportedActivity.filter((activityName) => {
    const fieldId = supportedObjectsToFieldIds[activityName];
    if (!fieldId) {
      return false;
    }

    if (fieldId === 'call') {
      return activityFlags.callEnabled !== false;
    }

    if (fieldId === 'meeting') {
      return activityFlags.meetingEnabled !== false;
    }

    if (fieldId === 'email') {
      if (activityFlags.emailAsTaskEnabled === true) {
        return false;
      }
      return activityFlags.emailEnabled !== false;
    }

    if (fieldId === 'task') {
      const callAsTaskEnabled = !hasCallsEntity && activityFlags.callEnabled === true;
      return (
        activityFlags.taskEnabled !== false ||
        activityFlags.emailAsTaskEnabled === true ||
        callAsTaskEnabled
      );
    }

    return true;
  });
};

export const getRequiredFieldsMappedToNonEditableFields = (
  fieldMappingsTableData: Record<string, ITableData>,
): { entity: string; fields: string[] }[] => {
  const violations: { entity: string; fields: string[] }[] = [];

  Object.entries(fieldMappingsTableData).forEach(([entityName, entityData]) => {
    const nonEditableRequiredFields = entityData.rowData.filter((field) => {
      // Check if field is required, has a mapping, and the mapped CRM field is non-editable
      return field.required && field.status && field.propertyAction === 'None';
    });

    if (nonEditableRequiredFields.length > 0) {
      violations.push({
        entity: entityName,
        fields: nonEditableRequiredFields.map((f) => f.label || f.sproutsField),
      });
    }
  });

  return violations;
};

export const isAllRequiredFieldsMapped = (
  fieldMappingsTableData: Record<string, ITableData>,
  requiredIntegrationFields: Record<string, string[]> = {},
  supportedObjects: string[] = [],
  globalSyncFlag?: Record<string, any>,
  providerName?: string,
) => {
  const mapEntityObject: Record<string, string> = {
    company: 'Companies',
    contacts: 'Contact',
    lead: 'Lead',
    opportunity: 'Deals',
    call: 'Call',
    task: 'Task',
    meeting: 'Meeting',
    sending: 'Sending',
    sequence_steps: 'Sequence Steps',
  };

  // Get filtered entities based on supportedObjects (framework-based approach)
  const filteredEntities = getFilteredFieldMappingsMocData(
    globalSyncFlag || {},
    supportedObjects,
    providerName,
  );
  const filteredEntityNames = new Set(filteredEntities.map((e) => e.entity));

  let allRequiredFieldsLeft = 0;
  // for sprouts fields - only check entities that are in supportedObjects
  Object.entries(fieldMappingsTableData).map(([entityName, field]) => {
    // Skip entities not in supportedObjects
    if (!filteredEntityNames.has(entityName)) {
      return;
    }
    const rowData = field.rowData;
    const requiredFieldsLeft = rowData ? getRequiredFieldsLeft(rowData) : 0;
    allRequiredFieldsLeft += requiredFieldsLeft;
  });

  // for integration fields - only check entities that are in supportedObjects
  Object.entries(requiredIntegrationFields).map(([key, value]) => {
    const entityName = mapEntityObject[key];
    // Skip entities not in supportedObjects
    if (!filteredEntityNames.has(entityName)) {
      return;
    }
    const rowData = fieldMappingsTableData[entityName]?.rowData;
    value.forEach((val) => {
      let isMapped: any = true;
      if (val.includes('/')) {
        isMapped = rowData?.find((field) => val.includes(field.integrationField));
      } else {
        isMapped = rowData?.find((field) => val === field.integrationField);
      }
      if (!isMapped) {
        allRequiredFieldsLeft += 1;
      }
    });
  });

  return allRequiredFieldsLeft === 0;
};

export const getSyncDataTimeStats = (
  lastSyncedAt: string,
  syncFrequency: PollingFrequencies,
  lastAutoSyncAt: string,
  timezone: string,
) => {
  const lastSync = timezoneConverter(lastSyncedAt, timezone, true, true);
  let interval = { value: 5, unit: 'minutes' as dayjs.ManipulateType };
  switch (syncFrequency) {
    case 'EVERY_FIVE_MINUTES':
      interval = { value: 5, unit: 'minutes' };
      break;
    case 'EVERY_FIFTEEN_MINUTES':
      interval = { value: 15, unit: 'minutes' };
      break;
    case 'EVERY_THIRTY_MINUTES':
      interval = { value: 30, unit: 'minutes' };
      break;
    case 'HOURLY':
      interval = { value: 1, unit: 'hours' };
      break;
    case 'EVERY_3_HOURS':
      interval = { value: 3, unit: 'hours' };
      break;
    case 'EVERY_6_HOURS':
      interval = { value: 6, unit: 'hours' };
      break;
    case 'EVERY_12_HOURS':
      interval = { value: 12, unit: 'hours' };
      break;
    case 'DAILY':
      interval = { value: 1, unit: 'days' };
      break;
    default:
  }

  // Use lastSyncedAt if valid, otherwise fall back to lastAutoSyncAt
  const baseTime =
    lastSyncedAt && dayjs.utc(lastSyncedAt).isValid() ? lastSyncedAt : lastAutoSyncAt;

  const nextSyncClock =
    timezoneConverter(
      dayjs.utc(baseTime).add(interval.value, interval.unit).toISOString(),
      timezone,
      true,
      true,
    )?.match(/\d{1,2}:\d{2}\s?[AP]M/i)?.[0] || '';

  return {
    lastSync,
    timeLeftToNextSync: dayjs.utc(baseTime).add(interval.value, interval.unit).fromNow(true),
    nextSync: nextSyncClock,
  };
};

export const getMapValuesType = (
  dropdownFieldTypes: string[],
  currentType?: string,
): MapValuesType | undefined => {
  if (!currentType) return undefined;

  if (dropdownFieldTypes.includes(currentType)) return 'Others';
};

export const getSproutsFieldType = (error: FieldTypes) => {
  let sproutsFieldType: string;
  switch (error) {
    case 'Date':
      sproutsFieldType = 'Date';
      break;
    case 'List':
      sproutsFieldType = 'Multi-select list';
      break;
    case 'Integer':
      sproutsFieldType = 'Number';
      break;
    case 'Object':
      sproutsFieldType = 'Single-select list';
      break;
    case 'Set':
      sproutsFieldType = 'Single-select list or Multi-select list';
      break;
    case 'Boolean':
      sproutsFieldType = 'Boolean';
      break;
    case 'Long':
      sproutsFieldType = 'Number';
      break;
    case 'Double':
      sproutsFieldType = 'Number';
      break;
    case 'String':
      sproutsFieldType = 'Text';
      break;
    default:
      sproutsFieldType = '';
      break;
  }
  return sproutsFieldType;
};

export const getSchedulePayload = (
  providerName: string,
  schedule_type: PollingFrequencies,
  supportedActivity: string[] = [],
  isLeadEnabled: boolean = true,
  activityFlags?: ActivityFlags,
  dealEnabled: boolean = true,
) => {
  // Special handling for Gong to use 30 minutes as default schedule instead of 5 minutes
  let finalScheduleType = schedule_type;
  if (providerName === 'GONG') {
    finalScheduleType = 'EVERY_THIRTY_MINUTES';
  }

  // Special handling for Mailchimp to use LISTS entity for inbound schedules
  if (providerName === 'MAILCHIMP') {
    return {
      provider: providerName,
      provider_entities: [`${providerName}_LIST`],
      schedule_type: finalScheduleType,
      cron_expression: '0 0 * * * *',
      reactivate_existing: true,
    };
  }

  const entityIdMap = getEntityIdMap(providerName);

  // Map supportedActivity to provider entities
  const filteredActivity = getFilteredSupportedActivity(supportedActivity, activityFlags);
  const providerEntities = filteredActivity
    .map((activityName) => {
      const fieldId = supportedObjectsToFieldIds[activityName];
      return entityIdMap[fieldId];
    })
    .filter(
      (entity) =>
        entity &&
        (entity !== `${providerName}_LEAD` || isLeadEnabled) &&
        (entity !== `${providerName}_DEALS` || dealEnabled),
    );

  return {
    provider: providerName,
    provider_entities: providerEntities,
    schedule_type: finalScheduleType,
    cron_expression: '0 0 * * * *',
    reactivate_existing: true,
  };
};

export const getPastDate = (period: string) => {
  const now = new Date();
  const pastDate = new Date(now);

  // Parse the period string to extract the value and unit
  const matchResult = (period || defaultHistoricSyncInterval).match(/(\d+)\s+(\w+)/);
  if (!matchResult) {
    throw new Error('Invalid format. Please use "<number> <unit>", e.g., "1 week", "2 months".');
  }
  const [, value, unit] = matchResult;

  // Calculate the past date based on the specified period
  switch (unit.toLowerCase()) {
    case 'day':
    case 'days':
      pastDate.setDate(now.getDate() - parseInt(value));
      break;
    case 'week':
    case 'weeks':
      pastDate.setDate(now.getDate() - parseInt(value) * 7);
      break;
    case 'month':
    case 'months':
      pastDate.setMonth(now.getMonth() - parseInt(value));
      break;
    default:
      throw new Error('Invalid time unit. Please use "day(s)", "week(s)", or "month(s)".');
  }

  // Format the dates as strings
  const query_end_timestamp = now.toISOString();
  pastDate.setHours(0, 0, 0, 0);
  const query_start_timestamp = pastDate.toISOString();

  return { query_end_timestamp, query_start_timestamp };
};

export const getOptionalSelectedField = (row: RowType, rowData: RowType[] = []) => {
  const optionalFields = rowData.filter(
    (field) => row.optionalWithValue === field.optionalWithValue,
  );
  return optionalFields?.find((field) => field.integrationField);
};

/**
 * Check if a sync condition is valid
 * A valid condition must have field and operator.
 * Zero-value operators (has_property, not_has_property) are valid without values.
 */
export const isValidSyncCondition = (condition: SyncCondition): boolean => {
  const hasField = !!(condition.field && condition.field.trim() !== '');
  const hasOperator = !!(condition.operator && condition.operator.trim() !== '');
  const isZeroValueOperator = ZERO_VALUE_OPERATORS.includes(condition.operator);
  const hasValues =
    isZeroValueOperator ||
    !!(condition.values && Array.isArray(condition.values) && condition.values.length > 0);
  return hasField && hasOperator && hasValues;
};

/**
 * Check if a sync condition's operator is compatible with its field type
 * Returns true if operator is valid for the field type
 */
export const isOperatorValidForFieldType = (
  operator: string,
  fieldType: string,
  conditionalSyncOperatorsByType: Record<string, any[]>,
  defaultConditionalSyncOperators: any[],
): boolean => {
  const normalizedFieldType = (fieldType || '').toLowerCase();
  const validOperators =
    conditionalSyncOperatorsByType[normalizedFieldType] || defaultConditionalSyncOperators;
  return validOperators.some((op) => op.value === operator);
};

/**
 * Get count of valid conditions from an array
 */
export const getValidConditionsCount = (conditions?: SyncCondition[]): number => {
  if (!conditions) return 0;
  return conditions.filter(isValidSyncCondition).length;
};

/**
 * Check if a condition is a duplicate of any existing conditions
 * A duplicate has the same field, operator, and values
 * Optimized with early returns and efficient comparisons
 */
export const isDuplicateCondition = (
  existingConditions: SyncCondition[],
  newCondition: SyncCondition,
): boolean => {
  // Early returns for invalid/incomplete conditions
  if (!newCondition.field || !newCondition.operator) {
    return false;
  }

  const newValues = newCondition.values || [];

  // Don't check for duplicates if new condition has no values
  if (newValues.length === 0) {
    return false;
  }

  return existingConditions.some((condition) => {
    // Quick field and operator comparison
    if (condition.field !== newCondition.field || condition.operator !== newCondition.operator) {
      return false;
    }

    const existingValues = condition.values || [];

    // Quick length check
    if (existingValues.length !== newValues.length) {
      return false;
    }

    // Compare each value (case-insensitive for strings)
    for (let i = 0; i < existingValues.length; i++) {
      const existingVal =
        typeof existingValues[i] === 'string' ? existingValues[i].toLowerCase() : existingValues[i];
      const newVal = typeof newValues[i] === 'string' ? newValues[i].toLowerCase() : newValues[i];

      if (existingVal !== newVal) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Merge custom fields with hardcoded outbound filterable fields
 * Custom fields from API are dynamically added to the static field definitions
 * Optimized with memoization-friendly structure
 */
export const mergeOutboundFilterableFields = (
  hardcodedFields: Record<string, { name: string; type: string }>,
  customFields: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>,
): Record<string, { name: string; type: string }> => {
  // If no custom fields, return hardcoded fields as-is
  if (!customFields || customFields.length === 0) {
    return hardcodedFields;
  }

  // Create a shallow copy to avoid mutating the original
  const merged = { ...hardcodedFields };

  // Add custom fields to the merged object
  customFields.forEach((customField) => {
    if (customField.fieldName && customField.dbColumnName) {
      // Use fieldName as the key (display label) and map to dbColumnName and fieldType
      merged[customField.fieldName] = {
        name: customField.dbColumnName,
        type: customField.fieldType,
      };
    }
  });

  return merged;
};

/**
 * Extract filterable fields from entity field mappings for outbound conditional sync
 * This creates a single source of truth by dynamically generating OutboundFilterableFields
 * from the field definitions in contacts.ts, company.ts, lead.ts
 *
 * @param entityMappings - Field mappings object (e.g., ContactsFieldMappings, CompanyFieldMappings)
 * @returns Object mapping field labels to { name (dbColumnName), type (fieldType) }
 */
export const extractFilterableFields = (entityMappings: {
  sproutsFields: Array<{ label: string; id: string; fieldType?: string; onlyOutbound?: boolean }>;
}): Record<string, { name: string; type: string }> => {
  const filterableFields: Record<string, { name: string; type: string }> = {};

  entityMappings.sproutsFields.forEach((field) => {
    if (field.fieldType) {
      filterableFields[field.label] = {
        name: field.id,
        type: field.fieldType,
      };
    }
  });

  return filterableFields;
};

/**
 * Get entity label from entity key
 * @param entityKey - Entity key like "HUBSPOT_ACCOUNT"
 * @param entityNameMapping - Entity name mapping
 * @returns Entity label like "Account"
 */
export const getEntityLabel = (
  entityKey: string,
  entityNameMapping: Record<string, string>,
): string => {
  const entityPart = entityKey?.split('_').pop() || '';
  return entityNameMapping[entityPart] || entityPart;
};

/**
 * Check if a field type is a picklist (dropdown) field
 * @param fieldType - Field type string
 * @returns true if field is a picklist type
 */
export const isPicklistField = (fieldType: string): boolean => {
  const normalizedType = fieldType.toLowerCase();
  return (
    normalizedType.includes('picklist') ||
    normalizedType.includes('multipicklist') ||
    normalizedType.includes('enumeration') ||
    normalizedType.includes('custom-preload-single-list') ||
    normalizedType.includes('custom-preload-multi-list')
  );
};

/**
 * Get initial sync configuration for a provider
 * Creates default sync config with create/update enabled for all entities
 * @param providerName - Provider name (e.g., 'HUBSPOT', 'SALESFORCE')
 * @returns Initial sync configuration object
 */
export const getInitialSyncConfig = (providerName: string) => {
  // Determine which entity type to use (LEAD for Salesforce/Microsoft, DEALS for others)
  const thirdEntity =
    providerName === 'SALESFORCE' || providerName === 'MICROSOFT' ? 'LEAD' : 'DEALS';

  return {
    [`${providerName}_ACCOUNT`]: { create: true, update: true },
    [`${providerName}_CONTACTS`]: { create: true, update: true },
    [`${providerName}_${thirdEntity}`]: { create: true, update: true },
  };
};

export const getSyncDirection = (providerName: string): SyncDirection | undefined => {
  const integrationKey = Object.keys(IntegrationsTools).find(
    (key) => IntegrationsTools[key].providerName === providerName,
  );
  return integrationKey ? IntegrationsTools[integrationKey].syncDirection : undefined;
};

export const applyEffectiveSyncFlag = (
  globalSyncFlag: GlobalSyncFlagResult,
  syncDirection: SyncDirection | undefined,
  type: string | undefined,
  supportedObjects: string[] | undefined,
): GlobalSyncFlagResult => {
  if (
    syncDirection === SyncDirection.OUTBOUND_ONLY ||
    (syncDirection === SyncDirection.BIDIRECTIONAL &&
      type === 'CMP' &&
      supportedObjects?.includes('Leads'))
  ) {
    return {
      ...globalSyncFlag,
      isLeadEnabled: true,
      syncNewSproutsContactsTo: 'Lead',
      intoSprouts: false,
      fromSprouts: true,
      isHistoricFetchEnabled: false,
    };
  }
  if (syncDirection === SyncDirection.INBOUND_ONLY) {
    return { ...globalSyncFlag, intoSprouts: true, fromSprouts: false };
  }
  return globalSyncFlag;
};
